import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  BookOpen,
  Puzzle,
  Globe2,
  Users,
  Dumbbell,
  Building,
  Monitor,
  Heart,
} from "lucide-react";

export const CourseList: React.FC = () => {
  const [language, setLanguage] = useState<"en" | "hi">("en");
  const toggleLanguage = () =>
    setLanguage((prev) => (prev === "en" ? "hi" : "en"));

  const curriculumPoints = [
    {
      icon: BookOpen,
      text: "Academic Subjects: Mathematics, Science, English, Social Science, Hindi",
    },
    {
      icon: Puzzle,
      text: "Reasoning & Aptitude: Logical, numerical, verbal skills",
    },
    {
      icon: Globe2,
      text: "General Knowledge: Current events, history, geography, culture",
    },
    {
      icon: Users,
      text: "Personality Development: Leadership, communication, teamwork",
    },
    {
      icon: Dumbbell,
      text: "Physical Training: Fitness, sports & outdoor activities",
    },
  ];

  const facilityPoints = [
    { icon: Building, text: "Modern classrooms with digital teaching aids" },
    { icon: Monitor, text: "Computer Lab with internet-enabled systems" },
    {
      icon: Heart,
      text: "Well-equipped indoor and outdoor sports and activity areas",
    },
    {
      icon: Users,
      text: "Comfortable hostel accommodation tailored for students traveling from outside",
    },
  ];

  const courses = [
    {
      id: 1,
      title: "Sainik / Military School Prep (Classes 3–5)",
      fees: "See Fee Structure",
      eligibility: "Class 2 passed",
    },
    {
      id: 2,
      title: "Sainik / Military School Prep (Classes 6–8)",
      fees: "See Fee Structure",
      eligibility: "Class 5 passed",
    },
  ];

  return (
    <main className="min-h-screen bg-gradient-to-br from-yellow-50 to-white px-4 py-12 text-gray-800">
      <div className="max-w-6xl mx-auto space-y-12">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h2 className="text-4xl font-bold text-blue-900">
            {language === "en" ? "Our Courses" : "हमारे कोर्स"}
          </h2>
          <button
            onClick={toggleLanguage}
            className="bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-semibold px-4 py-2 rounded"
          >
            {language === "en" ? "हिंदी में देखें" : "View in English"}
          </button>
        </div>

        {/* Curriculum Highlights */}
        <motion.section
          className="bg-white rounded-xl shadow p-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="bg-yellow-100 border-l-4 border-yellow-400 px-4 py-2 rounded mb-6 shadow-sm">
            <h3 className="text-2xl font-bold text-blue-900">
              {language === "en"
                ? "Curriculum Highlights"
                : "पाठ्यक्रम हाइलाइट"}
            </h3>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {curriculumPoints.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <item.icon className="w-6 h-6 text-blue-900" />
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Infrastructure */}
        <motion.section
          className="bg-white rounded-xl shadow p-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="bg-yellow-100 border-l-4 border-yellow-400 px-4 py-2 rounded mb-6 shadow-sm">
            <h3 className="text-2xl font-bold text-blue-900">
              {language === "en"
                ? "Infrastructure & Facilities"
                : "इन्फ्रास्ट्रक्चर एवं सुविधाएं"}
            </h3>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {facilityPoints.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <item.icon className="w-6 h-6 text-blue-900" />
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Course Table */}
        <motion.section
          className="overflow-x-auto bg-white rounded-xl shadow p-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
        >
          <div className="bg-yellow-100 border-l-4 border-yellow-400 px-4 py-2 rounded mb-6 shadow-sm">
            <h3 className="text-2xl font-bold text-blue-900 mb-4">
              {language === "en" ? "Course Offerings" : "कोर्स ऑफरिंग्स"}
            </h3>
          </div>
          <table className="w-full text-sm md:text-base border-collapse">
            <thead className="bg-blue-900 text-white text-left">
              <tr>
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">
                  {language === "en" ? "Course" : "कोर्स"}
                </th>
                <th className="px-4 py-3">
                  {language === "en" ? "Fees" : "शुल्क"}
                </th>
                <th className="px-4 py-3">
                  {language === "en" ? "Eligibility" : "योग्यता"}
                </th>
              </tr>
            </thead>
            <tbody>
              {courses.map((c) => (
                <tr key={c.id} className="border-b hover:bg-yellow-50">
                  <td className="px-4 py-3">{c.id}</td>
                  <td className="px-4 py-3">{c.title}</td>
                  <td className="px-4 py-3 text-blue-800 font-semibold">
                    {c.fees}
                  </td>
                  <td className="px-4 py-3">{c.eligibility}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.section>

        {/* Fee Structure */}
        <motion.section
          className="bg-white rounded-xl shadow p-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="bg-yellow-100 border-l-4 border-yellow-400 px-4 py-2 rounded mb-6 shadow-sm">
            <h3 className="text-2xl font-bold text-blue-900 mb-4">
              {language === "en" ? "Fee Structure" : "शुल्क संरचना"}
            </h3>
          </div>
          <ul className="list-disc list-inside text-gray-700 space-y-2">
            <li>
              <strong>Monthly Tuition Fee:</strong> ₹5,000
            </li>
            <li>
              <strong>Registration Fee:</strong> ₹10,000
            </li>
            <li>
              <strong>Hostel Fees:</strong> ₹60,000
            </li>
          </ul>
        </motion.section>
      </div>

      {/* WhatsApp CTA */}
      <a
        href="https://wa.me/919997450792?text=Hi%20I%20am%20interested%20in%20your%20academy%20program"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white px-5 py-3 rounded-full transition animate-pulse font-semibold shadow-lg"
        title="Chat on WhatsApp"
      >
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
          alt="WhatsApp"
          className="h-8 w-8"
        />
        <span className="hidden sm:inline">Chat</span>
      </a>
    </main>
  );
};
